<template>
  <div class="print-setting-address-sheet">
    <validation-observer
      class="validation-observer"
      tag="div"
      v-slot="{ errors, invalid }"
    >
      <div class="form">
        <div class="form-item">
          <div class="label">用紙サイズ</div>
          <div class="input-field">
            <div class="radio-buttons">
              <base-radio-button
                v-for="option in radios.paperSize"
                class="radio paper-size"
                :data-test="['radio', 'paper-size', `${option.eachValue}`]"
                :input-styles="radioButtonStyle"
                :key="'paper-size' + option.id"
                :option="option"
                v-model="printSetting.addressSheetPaperSize"
              />
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">印刷の向き</div>
          <div class="input-field">
            <div class="radio-buttons">
              <base-radio-button
                v-for="option in radios.addressLayout"
                class="radio address-layout"
                :data-test="['radio', 'address-layout', `${option.eachValue}`]"
                :disabled="isPostCard"
                :input-styles="radioButtonStyle"
                :key="option.id"
                :option="option"
                v-model="printSetting.addressSheetVerticalFlg"
              />
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">レイアウト</div>
          <div class="input-field">
            <div class="two-values">
              <select-box-form
                data-test="select layout-rows"
                :disabled="isPostCard"
                :select-data="layoutRowOptions"
                :styles="{ width: '60px' }"
                v-model="printSetting.addressSheetRows"
                >行数</select-box-form
              >
              <select-box-form
                data-test="select layout-columns"
                :disabled="isPostCard"
                :select-data="layoutColumnOptions"
                :styles="{ width: '60px' }"
                v-model="printSetting.addressSheetColumns"
                >列数</select-box-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">
            余白（mm）<span
              class="hint"
              data-test="hint margins"
              @click="showLayout = !showLayout"
              >?</span
            >
          </div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input top-bottom-margins"
                :disabled="isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'topBottomMargins'"
                @blur="blur('addressSheetTopBottomMargins')"
                v-model.number="printSetting.addressSheetTopBottomMargins"
                >上下余白</text-input-form
              >
              <text-input-form
                data-test="input left-right-margins"
                :disabled="isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'leftRightMargins'"
                @blur="blur('addressSheetLeftRightMargins')"
                v-model.number="printSetting.addressSheetLeftRightMargins"
                >左右余白</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">
            間隔（mm）<span
              class="hint"
              data-test="hint gaps"
              @click="showLayout = !showLayout"
              >?</span
            >
          </div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input vertical-margins"
                :disabled="isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'verticalMargins'"
                @blur="blur('addressSheetVerticalMargins')"
                v-model.number="printSetting.addressSheetVerticalMargins"
                >行間隔</text-input-form
              >
              <text-input-form
                data-test="input horizontal-margins"
                :disabled="isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'horizontalMargins'"
                @blur="blur('addressSheetHorizontalMargins')"
                v-model.number="printSetting.addressSheetHorizontalMargins"
                >列間隔</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">はがきでの郵便番号の位置（mm）</div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input owner-postal-code-vertical-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardOwnerPostalCodeVerticalPosition'"
                @blur="blur('postCardOwnerPostalCodeVerticalPosition')"
                v-model.number="
                  printSetting.postCardOwnerPostalCodeVerticalPosition
                "
                >上</text-input-form
              >
              <text-input-form
                data-test="input owner-postal-code-horizontal-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardOwnerPostalCodeHorizontalPosition'"
                @blur="blur('postCardOwnerPostalCodeHorizontalPosition')"
                v-model.number="
                  printSetting.postCardOwnerPostalCodeHorizontalPosition
                "
                >左</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">はがきでの飼主住所の位置（mm）</div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input owner-address-vertical-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardOwnerAddressVerticalPosition'"
                @blur="blur('postCardOwnerAddressVerticalPosition')"
                v-model.number="
                  printSetting.postCardOwnerAddressVerticalPosition
                "
                >上</text-input-form
              >
              <text-input-form
                data-test="input owner-address-horizontal-position"
                :vid="'postCardOwnerAddressHorizontalPosition'"
                :maxlength="3"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :styles="{ width: '60px' }"
                :full-width-numbers-to-half-width-numbers="true"
                :disabled="!isPostCard"
                @blur="blur('postCardOwnerAddressHorizontalPosition')"
                v-model.number="
                  printSetting.postCardOwnerAddressHorizontalPosition
                "
                >左</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">はがきでの飼主名の位置（mm）</div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input owner-name-vertical-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardOwnerNameVerticalPosition'"
                @blur="blur('postCardOwnerNameVerticalPosition')"
                v-model.number="printSetting.postCardOwnerNameVerticalPosition"
                >上</text-input-form
              >
              <text-input-form
                data-test="input owner-name-horizontal-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardOwnerNameHorizontalPosition'"
                @blur="blur('postCardOwnerNameHorizontalPosition')"
                v-model.number="
                  printSetting.postCardOwnerNameHorizontalPosition
                "
                >左</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">はがきでの医院情報の位置（mm）</div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input sender-vertical-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardSenderVerticalPosition'"
                @blur="blur('postCardSenderVerticalPosition')"
                v-model.number="printSetting.postCardSenderVerticalPosition"
                >上</text-input-form
              >
              <text-input-form
                data-test="input sender-horizontal-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardSenderHorizontalPosition'"
                @blur="blur('postCardSenderHorizontalPosition')"
                v-model.number="printSetting.postCardSenderHorizontalPosition"
                >左</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="form-item">
          <div class="label">はがきでの医院の郵便番号の位置（mm）</div>
          <div class="input-field">
            <div class="two-values">
              <text-input-form
                data-test="input sender-postal-code-vertical-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardSenderPostalCodeVerticalPosition'"
                @blur="blur('postCardSenderPostalCodeVerticalPosition')"
                v-model.number="
                  printSetting.postCardSenderPostalCodeVerticalPosition
                "
                >上</text-input-form
              >
              <text-input-form
                data-test="input sender-postal-code-horizontal-position"
                :disabled="!isPostCard"
                :full-width-numbers-to-half-width-numbers="true"
                :maxlength="3"
                :styles="{ width: '60px' }"
                :validation-rules="{ requiredRule: true, intRule: true }"
                :vid="'postCardSenderPostalCodeHorizontalPosition'"
                @blur="blur('postCardSenderPostalCodeHorizontalPosition')"
                v-model.number="
                  printSetting.postCardSenderPostalCodeHorizontalPosition
                "
                >左</text-input-form
              >
            </div>
          </div>
        </div>
        <div class="post-card-sender-check">
          <base-check-box
            data-test="checked post-card-sender-flg"
            :disabled="!isPostCard"
            :is-checked="printSetting.postCardSenderFlg"
            :label-text="'はがきでの医院情報を印刷する'"
            :num-flg="true"
            @input="check"
          />
        </div>
        <div class="form-item">
          <div class="label">印刷プレビュー</div>
          <div class="buttons">
            <base-button-border-orange
              data-test="preview-address-sheet"
              :disabled="isButtonDisabled(errors, invalid)"
              :styles="{ width: '120px', height: '44px' }"
              @click="openPdf"
              >宛名シート印刷</base-button-border-orange
            >
          </div>
        </div>
        <div v-if="showLayout" class="layout-image">
          <img
            alt="address-sheet-margins"
            src="@/assets/images/address_sheet_margins.png"
            width="400px"
            height="320px"
          />
        </div>
      </div>
      <div v-if="!!getSizeErrorMessage(errors)" class="size-error">
        <span class="text" v-html="getSizeErrorMessage(errors)"></span>
      </div>
      <div class="registration">
        <base-button-medium-orange
          v-if="!lookOnlyFlg"
          data-test="registration-button"
          :disabled="isButtonDisabled(errors, invalid)"
          @click="updateData"
          >登録</base-button-medium-orange
        >
      </div>
    </validation-observer>
    <announce-popup
      v-if="resultPopup.isOpened"
      data-test="result-popup"
      v-bind="resultPopup"
      @close="resultPopup.close"
      >{{ resultPopup.message }}</announce-popup
    >
    <unsaved-leave-popup />
  </div>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonBorderOrange from '@/components/parts/atoms/BaseButtonBorderOrange'
import BaseButtonMediumOrange from '@/components/parts/atoms/BaseButtonMediumOrange'
import BaseCheckBox from '@/components/parts/atoms/BaseCheckBox'
import BaseRadioButton from '@/components/parts/atoms/BaseRadioButton'
import CheckInputDifference from '@/components/mixins/CheckInputDifference'
import SelectBoxForm from '@/components/parts/molecules/SelectBoxForm'
import TextInputForm from '@/components/parts/molecules/TextInputForm'
import UnsavedLeavePopup from '@/components/popups/UnsavedLeavePopup'
import dedent from 'dedent'
import { PAPER_SIZES } from '@/utils/define'
import { ValidationObserver } from 'vee-validate'
import { mapGetters } from 'vuex'
import { printAddressSheet } from '@/utils/print_address_sheet'

export default {
  name: 'PrintSettingAddressSheet',

  components: {
    AnnouncePopup,
    BaseButtonBorderOrange,
    BaseButtonMediumOrange,
    BaseCheckBox,
    BaseRadioButton,
    SelectBoxForm,
    TextInputForm,
    UnsavedLeavePopup,
    ValidationObserver
  },

  mixins: [CheckInputDifference],

  data() {
    return {
      cellHeight: undefined,
      cellWidth: undefined,
      inputAttributes: [
        'addressSheetPaperSize',
        'addressSheetVerticalFlg',
        'addressSheetRows',
        'addressSheetColumns',
        'addressSheetTopBottomMargins',
        'addressSheetLeftRightMargins',
        'addressSheetVerticalMargins',
        'addressSheetHorizontalMargins',
        'postCardSenderFlg',
        'postCardOwnerPostalCodeVerticalPosition',
        'postCardOwnerPostalCodeHorizontalPosition',
        'postCardOwnerNameVerticalPosition',
        'postCardOwnerNameHorizontalPosition',
        'postCardOwnerAddressVerticalPosition',
        'postCardOwnerAddressHorizontalPosition',
        'postCardSenderVerticalPosition',
        'postCardSenderHorizontalPosition',
        'postCardSenderPostalCodeVerticalPosition',
        'postCardSenderPostalCodeHorizontalPosition'
      ],
      previews: {
        owner: {
          id: 1,
          lastName: 'プレビュー',
          firstName: '飼主',
          prefectureId: 13,
          postalCode: '1000000',
          address: '〇〇〇市〇〇〇町〇〇〇丁目',
          building: 'プレビュービル〇〇〇号室'
        },
        patient: {
          name: 'プレビュー患者',
          showId: '1',
          ownerId: 1
        }
      },
      printSetting: {},
      radioButtonStyle: { paddingLeft: '20px' },
      radios: {
        paperSize: [
          { id: 'A4', eachValue: 'A4', labelName: 'A4' },
          { id: 'B5', eachValue: 'B5', labelName: 'B5' },
          { id: 'A5', eachValue: 'A5', labelName: 'A5' },
          { id: 'PC', eachValue: 'PC', labelName: 'はがき' }
        ],
        addressLayout: [
          { id: 'vertical', eachValue: 1, labelName: '縦' },
          { id: 'horizontal', eachValue: 0, labelName: '横' }
        ]
      },
      resultPopup: {
        buttons: ['閉じる'],
        close: this.closeResultPopup,
        isOpened: false,
        message: '',
        title: '',
        type: ''
      },
      showLayout: false,
      updating: false
    }
  },

  computed: {
    ...mapGetters({
      clinic: 'clinic/getData',
      getMasterDatum: 'master/getDataById',
      lookOnlyFlg: 'auth/lookOnlyFlg',
      prefectures: 'master/getPrefectures'
    }),
    invalidAddressSheetHeight() {
      // 高さ210mmで40行入力できたので1行あたり5.25mmとした。
      const minCellHeight = 5.25 * 5
      return this.cellHeight < minCellHeight
    },
    invalidAddressSheetWidth() {
      const minCellWidth = 48.3
      return this.cellWidth < minCellWidth
    },
    isPostCard() {
      return this.printSetting.addressSheetPaperSize === 'PC'
    },
    layoutColumnOptions() {
      return [...Array(6).keys()].map(i => {
        return { id: i + 1, name: i + 1 }
      })
    },
    layoutRowOptions() {
      return [...Array(11).keys()].map(i => {
        return { id: i + 1, name: i + 1 }
      })
    }
  },

  watch: {
    printSetting: {
      deep: true,
      handler() {
        const { addressSheetPaperSize: paperSize } = this.printSetting
        const {
          addressSheetVerticalFlg: verticalFlg,
          addressSheetRows: rows,
          addressSheetColumns: columns,
          addressSheetTopBottomMargins: topBottomMargins,
          addressSheetLeftRightMargins: leftRightMargins,
          addressSheetVerticalMargins: verticalMargins,
          addressSheetHorizontalMargins: horizontalMargins
        } =
          paperSize === 'PC'
            ? {
                addressSheetVerticalFlg: 1,
                addressSheetRows: 1,
                addressSheetColumns: 1,
                addressSheetTopBottomMargins: 5,
                addressSheetLeftRightMargins: 5,
                addressSheetVerticalMargins: 0,
                addressSheetHorizontalMargins: 0
              }
            : this.printSetting
        const paperWidth = verticalFlg
          ? PAPER_SIZES[paperSize]?.x
          : PAPER_SIZES[paperSize]?.y
        const paperHeight = verticalFlg
          ? PAPER_SIZES[paperSize]?.y
          : PAPER_SIZES[paperSize]?.x
        const availableWidth =
          paperWidth - leftRightMargins * 2 - horizontalMargins * (columns - 1)
        const availableHeight =
          paperHeight - topBottomMargins * 2 - verticalMargins * (rows - 1)
        this.cellWidth = availableWidth / columns
        this.cellHeight = availableHeight / rows
      }
    }
  },

  created() {
    this.setData()
  },

  methods: {
    blur(key) {
      if (this.printSetting[key] === '') {
        this.printSetting[key] = 0
      }
    },
    check(isChecked) {
      this.printSetting.postCardSenderFlg = isChecked
    },
    closeResultPopup() {
      this.resultPopup.isOpened = false
    },
    getSizeErrorMessage(errors) {
      const { composition, direction } = this.makeSizeErrorItems(errors)
      const message =
        composition && direction
          ? dedent`
              ※${composition}から想定される宛名シートの${direction}が「デフォルト用紙サイズ」の${direction}を超えています。
              「デフォルト用紙サイズ」の${direction}におさまるよう値を変更してください。
            `
          : ''
      return message
    },
    hasTextInputError(errors) {
      const vids = [
        'horizontalMargins',
        'leftRightMargins',
        'postCardOwnerAddressHorizontalPosition',
        'postCardOwnerAddressVerticalPosition',
        'postCardOwnerNameHorizontalPosition',
        'postCardOwnerNameVerticalPosition',
        'postCardOwnerPostalCodeHorizontalPosition',
        'postCardOwnerPostalCodeVerticalPosition',
        'postCardSenderHorizontalPosition',
        'postCardSenderPostalCodeVerticalPosition',
        'postCardSenderVerticalPosition',
        'topBottomMargins',
        'verticalMargins',
        'postCardSenderPostalCodeHorizontalPosition'
      ]
      return vids.some(vid => errors[vid]?.length > 0)
    },
    isButtonDisabled(errors, invalid) {
      return invalid || this.updating || !!this.getSizeErrorMessage(errors)
    },
    makePrintOptions() {
      const basePrintOptions = {
        paperSize: this.printSetting.addressSheetPaperSize,
        verticalFlg: this.printSetting.addressSheetVerticalFlg,
        rows: this.printSetting.addressSheetRows,
        columns: this.printSetting.addressSheetColumns,
        horizontalMargins: this.printSetting.addressSheetHorizontalMargins,
        verticalMargins: this.printSetting.addressSheetVerticalMargins,
        leftRightMargins: this.printSetting.addressSheetLeftRightMargins,
        topBottomMargins: this.printSetting.addressSheetTopBottomMargins,
        postCardSenderFlg: this.printSetting.postCardSenderFlg,
        postCardOwnerPostalCodeVerticalPosition: this.printSetting
          .postCardOwnerPostalCodeVerticalPosition,
        postCardOwnerPostalCodeHorizontalPosition: this.printSetting
          .postCardOwnerPostalCodeHorizontalPosition,
        postCardOwnerNameVerticalPosition: this.printSetting
          .postCardOwnerNameVerticalPosition,
        postCardOwnerNameHorizontalPosition: this.printSetting
          .postCardOwnerNameHorizontalPosition,
        postCardOwnerAddressVerticalPosition: this.printSetting
          .postCardOwnerAddressVerticalPosition,
        postCardOwnerAddressHorizontalPosition: this.printSetting
          .postCardOwnerAddressHorizontalPosition,
        postCardSenderVerticalPosition: this.printSetting
          .postCardSenderVerticalPosition,
        postCardSenderHorizontalPosition: this.printSetting
          .postCardSenderHorizontalPosition,
        postCardSenderPostalCodeVerticalPosition: this.printSetting
          .postCardSenderPostalCodeVerticalPosition,
        postCardSenderPostalCodeHorizontalPosition: this.printSetting
          .postCardSenderPostalCodeHorizontalPosition
      }
      const printOptions = this.isPostCard
        ? {
            ...basePrintOptions,
            rows: 1,
            columns: 1,
            verticalFlg: 1,
            topBottomMargins: 5,
            leftRightMargins: 5,
            horizontalMargins: 0,
            verticalMargins: 0
          }
        : { ...basePrintOptions }
      return printOptions
    },
    makeSizeErrorItems(errors) {
      const [composition, direction] = this.showSheetWidthError(errors)
        ? this.isPostCard
          ? ['「左右余白」', '横幅']
          : ['「印刷の向き」「列数」「左右余白」「列間隔」', '横幅']
        : this.showSheetHeightError(errors)
        ? this.isPostCard
          ? ['「上下余白」', '縦幅']
          : ['「印刷の向き」「行数」「上下余白」「行間隔」', '縦幅']
        : ['', '']
      return { composition, direction }
    },
    openPdf() {
      const printOptions = this.makePrintOptions()
      const cellsPerPage =
        this.printSetting.addressSheetRows *
        this.printSetting.addressSheetColumns
      const baseArgs = {
        printOptions,
        cellWidth: this.cellWidth,
        cellHeight: this.cellHeight,
        owners: new Array(cellsPerPage).fill(this.previews.owner),
        patientsMap: new Map([
          [this.previews.patient.ownerId, [this.previews.patient]]
        ]),
        prefectures: this.prefectures
      }
      const args = this.isPostCard
        ? { ...baseArgs, clinic: this.clinic }
        : { ...baseArgs }
      printAddressSheet(args)
    },
    setData() {
      this.printSetting = { ...this.$store.getters['printSetting/getData'] }
      this.mixinInputData = this.printSetting
      this.mixinSetInitialData()
    },
    showSheetHeightError(errors) {
      return (
        !this.hasTextInputError(errors) &&
        !this.invalidAddressSheetWidth &&
        this.invalidAddressSheetHeight
      )
    },
    showSheetWidthError(errors) {
      return !this.hasTextInputError(errors) && this.invalidAddressSheetWidth
    },
    async updateData() {
      this.updating = true
      const res = await this.$store.dispatch(
        'printSetting/update',
        this.printSetting
      )
      if (res === true) {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '編集しました',
          title: '完了',
          type: 'success'
        }
        this.setData()
      } else {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '編集に失敗しました',
          title: '失敗',
          type: 'failure'
        }
      }
      this.updating = false
    }
  }
}
</script>

<style lang="scss" scoped>
$input-line-height: 33px;
.print-setting-address-sheet {
  margin-top: 30px;
  padding-bottom: 100px;
  text-align: left;
  font-size: 15px;
  > .validation-observer {
    > .form {
      position: relative;
      display: flex;
      flex-direction: column;
      row-gap: 30px;
      > .form-item {
        display: flex;
        align-items: flex-start;
        > .label {
          display: flex;
          align-items: center;
          width: 300px;
          min-height: $input-line-height;
          font-feature-settings: 'palt';
          > .hint {
            margin-left: 6px;
            text-align: center;
            display: inline-block;
            font-size: 13px;
            width: 20px;
            height: 20px;
            background-color: #{$pumpkin};
            color: #{$white};
            border-radius: 50%;
            @include hover();
          }
        }
        > .input-field {
          flex: 1;
          display: flex;
          align-items: center;
          min-height: $input-line-height;
          > .radio-buttons {
            display: flex;
          }
          > .two-values {
            display: flex;
            width: 380px;
            > * {
              flex: 1;
            }
          }
        }
        > .buttons {
          flex: 1;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          column-gap: 10px;
          row-gap: 10px;
          ::v-deep .base-button-border-orange {
            > button {
              &:active {
                margin: 2px auto 0 auto;
                max-height: 42px;
              }
            }
          }
        }
      }
      > .layout-image {
        position: absolute;
        z-index: 1;
        top: 190px;
        left: 650px;
        height: 320px;
        background-color: #{$white};
      }
    }
    > .size-error {
      position: relative;
      > .text {
        position: absolute;
        line-height: 13px;
        font-size: 12px;
        color: #{$tomato};
        padding-left: 1em;
        text-indent: -1em;
      }
    }
    > .post-card-sender-check {
      margin-top: 32px;
      display: inline-block;
    }
    > .registration {
      margin-top: 70px;
      display: flex;
      justify-content: center;
      max-width: 1200px;
    }
  }
}
</style>
